import { Injectable, Type, inject, signal } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Router, Routes, RunGuardsAndResolvers } from '@angular/router';
import { Observable, catchError, map, of, tap } from 'rxjs';

import { AppRoutesConfig, RouteDefinition } from './route-config.model';
import { SILENT_ERROR } from './error.interceptor';
import { BaseOutput } from '../shared/models/fink.model';
import { pageConfigResolver } from './page-config.resolver';

// Page config is static per route (keyed by data.configCode, never by :id) — only rerun the
// resolver on a genuine new route activation, not on a param-only change of a reused component
// (e.g. navigating between two detail-page records reuses the component without a refetch).
const runResolversOnlyOnRouteChange: RunGuardsAndResolvers = () => false;

// Add one entry here per page component a backend route definition can point to.
const COMPONENT_MAP: Record<string, () => Promise<Type<any>>> = {
  DashboardComponent:              () => import('../pages/dashboard/dashboard.component').then(m => m.DashboardComponent),
  GenericListPageComponent:        () => import('../pages/generic-list-page/generic-list-page.component').then(m => m.GenericListPageComponent),
  GenericDetailPageComponent:      () => import('../pages/generic-detail-page/generic-detail-page.component').then(m => m.GenericDetailPageComponent),
  // Livello 2 (FRAMEWORK.md §3): pagina dedicata, sempre pilotata dalla sua riga ui_config.
  PraticaDetailPageComponent:      () => import('../pages/pratica-detail-page/pratica-detail-page.component').then(m => m.PraticaDetailPageComponent),
};

@Injectable({ providedIn: 'root' })
export class RouteConfigService {
  private http   = inject(HttpClient);
  private router = inject(Router);

  readonly loadError = signal<string | null>(null);

  load(): Observable<void> {
    // a new attempt (e.g. after a login) must not keep the failure of the previous one
    this.loadError.set(null);
    return this.http.get<BaseOutput<{ config: AppRoutesConfig }>>('/backoffice/v1/uiConfig/code/app-routes', {
      context: new HttpContext().set(SILENT_ERROR, true),
    }).pipe(
      tap(res => this.applyRoutes(res.data?.config?.routes ?? [])),
      catchError((err: HttpErrorResponse) => {
        this.loadError.set(err.error?.error ?? 'Impossibile caricare le rotte dell\'applicazione');
        return of(null);
      }),
      map(() => void 0),
    );
  }

  private applyRoutes(routeDefs: RouteDefinition[]): void {
    const children: Routes = [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
    ];

    for (const def of routeDefs) {
      const loader = COMPONENT_MAP[def.component];
      if (!loader) continue;
      children.push({
        path: def.path,
        loadComponent: loader,
        ...(def.data ? { data: def.data } : {}),
        resolve: { uiConfig: pageConfigResolver },
        runGuardsAndResolvers: runResolversOnlyOnRouteChange,
      });
    }

    // Swap children of the layout route (first entry in router config)
    const config = this.router.config.slice();
    if (config.length > 0) {
      config[0] = { ...config[0], children };
    }
    this.router.resetConfig(config);
  }
}
